import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getAssignedUserListToTasks, getPersonById, getSelectedPersons } from "@common-reducers/TaskSelectors";
import { taskActions } from "@common-reducers/TasksReducer";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { namespace } from "@common-config/constants";
import { usePortalContainer } from "@/common/hooks/use-portal-container";
import { Check, UserCircle, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PersonAvatar from "@common-components/tasks/person-avatar/person-avatar";
import { userDisplayName } from "@common-models/user";

export default function PersonFilterShadcn() {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const currentAssignedMemberList = useAppSelector(getAssignedUserListToTasks);
    const personUserId = useAppSelector((state) => getSelectedPersons(state));
    const person = useAppSelector(getPersonById);
    
    // Portal logic
    const localPortalContainer = usePortalContainer();
    const [portalContainer, setPortalContainer] = useState<HTMLElement | ShadowRoot | null>(null);

    useEffect(() => {
        const globalPortal = document.getElementById('labels-modals')?.shadowRoot?.querySelector('.btcss-portal-container');
        if (globalPortal) {
            setPortalContainer(globalPortal as HTMLElement);
        }
    }, []);

    const clearPersonFilter = (e: React.MouseEvent) => {
        e.stopPropagation();
        dispatch(taskActions.setPersonFilter(""));
    };

    const handlePersonSelect = (selectedId: string) => {
        dispatch(taskActions.setPersonFilter(personUserId !== selectedId ? selectedId : ''));
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button 
                    variant="ghost" 
                    size="sm"
                    className={`h-9 px-3 ${personUserId ? 'bg-accent opacity-100 shadow-sm' : 'opacity-50 hover:opacity-100'}`}
                >
                    {personUserId ? (
                        <div className="flex items-center gap-2">
                             <PersonAvatar
                                size={16}
                                email={person?.email}
                                profileImg={person?.profile?.profileImg}
                                lastName={person?.profile?.lastName}
                                firstName={person?.profile?.firstName}
                            />
                            <span className="text-sm font-medium">{t('personButtonFilter.personButton')}</span>
                            <div 
                                role="button"
                                onClick={clearPersonFilter}
                                className="ml-1 hover:bg-slate-200 rounded-full p-0.5"
                            >
                                <X className="h-3 w-3 text-muted-foreground" />
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 text-muted-foreground">
                            <UserCircle className="h-4 w-4" />
                            <span className="text-sm font-medium">{t('personButtonFilter.personButton')}</span>
                        </div>
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
                align="start" 
                side="bottom"
                className="w-56 z-[1550]"
                container={portalContainer || localPortalContainer}
            >
                <div className="p-1 flex flex-col gap-1">
                     {currentAssignedMemberList.length > 0 ? (
                        currentAssignedMemberList.map((member, i) => {
                            const isSelected = personUserId === member._id;
                            return (
                                <DropdownMenuItem 
                                    key={i} 
                                    onClick={() => handlePersonSelect(member._id ?? '')}
                                    className="flex items-center justify-between cursor-pointer px-2 py-1.5"
                                >
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <PersonAvatar
                                            size={20}
                                            email={member.email}
                                            profileImg={member?.profile?.profileImg}
                                            lastName={member?.profile?.lastName}
                                            firstName={member?.profile?.firstName}
                                        />
                                        <span className="text-sm truncate">
                                            {member.profile ? userDisplayName(member.profile) : (member.email || member.firstName || t('personButtonFilter.unknown'))}
                                        </span>
                                    </div>
                                    {isSelected && <Check className="h-4 w-4 text-primary ml-2 flex-shrink-0" />}
                                </DropdownMenuItem>
                            );
                        })
                    ) : (
                        <div className="p-2 text-sm text-center text-muted-foreground">
                            {t('personButtonFilter.noAssignedMembers')}
                        </div>
                    )}
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
